import moment from 'moment-timezone'
import { currentPricing } from '../docs/memberBenefits'
import { scheduleMembership, createRenewalSubscription } from '../../store/actions/membershipActions'

// The currentPricing entry each membership type renews onto
const PRICING_KEYS = {
    'Golf': 'Golf',
    'Social': 'Social',
    'Golf Add-on': 'Golf_Addon',
    'Social Add-on': 'Social_Addon'
}

/**
 * The renewal option offered to a member of `type` (as returned by getType in
 * myAccount.js), or undefined for a type that has no renewal plan.
 */
export const getRenewalOption = (type, pricing = currentPricing) => {
    const plan = pricing[PRICING_KEYS[type]]
    if (!plan) return undefined

    return {
        priceId: plan.priceId,
        displayName: plan.displayName,
        startDate: plan.startDate,
        endDate: plan.endDate,
        disabled: false
    }
}

/**
 * Handles the scheduleMembership callback. Only a successful schedule counts as
 * a renewal: onScheduled runs. Otherwise onError runs and nothing else does.
 *
 * The add-ons are not emailed from here. The renewal-created webhook (flow B,
 * dashboards-api) emails each accepted add-on once the schedule exists.
 */
export const handleRenewalScheduled = (err, { onScheduled, onError }) => {
    if (err) {
        onError(err)
        return
    }
    onScheduled()
}

export const RENEWAL_TZ = 'America/New_York'
export const RENEWAL_MODES = { LIVE: 'live', SCHEDULE: 'schedule' }
const DATE_FORMAT = 'MM/DD/YYYY'

/**
 * The term end for a renewal starting at `start`: Dec 31 of the start year, in
 * America/New_York, as MM/DD/YYYY (the format the schedule endpoint parses).
 *
 * A start on Dec 31 itself would give an empty term, so the end is the first
 * Dec 31 after the start: next year's. Memberships created by the schedule
 * endpoint end on 12/31, so their renewal starts that day.
 *
 * Mirrors PrimaryRenewalService.termEnd in dashboards-api.
 */
export const renewalTermEnd = (start) => {
    const startNy = moment.tz(start, DATE_FORMAT, RENEWAL_TZ).startOf('day')
    let end = moment.tz({ year: startNy.year(), month: 11, day: 31 }, RENEWAL_TZ).startOf('day')
    if (!end.isAfter(startNy)) {
        end = moment.tz({ year: startNy.year() + 1, month: 11, day: 31 }, RENEWAL_TZ).startOf('day')
    }
    return end.format(DATE_FORMAT)
}

/**
 * How the primary renews (add-on renewal spec, section 7).
 *
 * - Still active: a SCHEDULE that starts the day the current subscription ends
 *   (cancelAtDate, MM/DD/YYYY) and ends Dec 31 of that year.
 * - Expired: a LIVE subscription that starts now; the server sets its cancel_at
 *   to Dec 31 the same way.
 *
 * Returns { mode, startDate, endDate } (MM/DD/YYYY), or null when there is
 * nothing to renew.
 */
export const planRenewal = ({ expired, cancelAtDate, now = new Date() }) => {
    if (expired) {
        const startDate = moment.tz(now, RENEWAL_TZ).format(DATE_FORMAT)
        return { mode: RENEWAL_MODES.LIVE, startDate, endDate: null }
    }
    if (cancelAtDate) {
        return { mode: RENEWAL_MODES.SCHEDULE, startDate: cancelAtDate, endDate: null }
    }
    // Active with no end date: nothing to renew yet (the Renew button is hidden)
    return null
}

/**
 * The thunk that creates the renewal in Stripe for `mode`: a live subscription
 * for an expired member, otherwise a schedule. Both end behaviours are cancel.
 */
export const startRenewal = ({ mode, loginToken, customerId, startDate, endDate, priceId }, cb) => (
    mode === RENEWAL_MODES.LIVE
        ? createRenewalSubscription(loginToken, customerId, priceId, cb)
        : scheduleMembership(loginToken, customerId, 'cancel', startDate, endDate, priceId, cb)
)

/**
 * After signing: store the add-on billing answer first (members eligible for
 * add-ons only), then create the renewal.
 *
 * The answer is saved before the Stripe write because the renewal-created
 * webhook starts the add-on emails, and those read paysForAddOns. A failed save
 * must not block the renewal: saveAddOnBilling reports its own error and then
 * calls done.
 */
export const renewAfterSigning = ({ isEligibleForAddon, saveAddOnBilling, renew }) => {
    if (isEligibleForAddon !== 'true') {
        renew()
        return
    }
    saveAddOnBilling(() => renew())
}

/**
 * The renewal window the primary's Renew Membership button follows, and the
 * add-on rows' Renew buttons with it (add-on renewal spec, section 3):
 * cancel_at is 0-90 days away, or the membership has expired.
 *
 *   cancelAtScheduled - a subscription has a cancel_at
 *   daysToExpire      - whole days from now to that cancel_at
 *   expired           - accountCancelled in myAccount.js
 */
export const RENEWAL_WINDOW_DAYS = 90

export const isInRenewalWindow = ({ cancelAtScheduled, daysToExpire, expired }) =>
    !!expired || (!!cancelAtScheduled && daysToExpire >= 0 && daysToExpire <= RENEWAL_WINDOW_DAYS)
